import { forwardRef, useId, type CSSProperties } from 'react';

import grain from '../assets/grain.png';
import holoUp from '../assets/holo-up.png';
import { cx } from '../cx';
import { CORES, EFEITOS } from '../tokens';
import { useHolo } from './holo';
import { HofMedalGlyph, HofMedalGradient } from './HofMedal';

/**
 * Nivel da conta, que da a cor da carta: cinzento para quem ainda e
 * utilizador, vermelho escuro para membro, vermelho Grow uP para associado.
 * Um streamer ativo leva um selo roxo (o da Twitch): ver `streamer`.
 */
export type PlayerCardTier = 'user' | 'member' | 'associate';

export type PlayerCardStat = {
  /** Etiqueta curta, em maiusculas na carta («HRS ARENA»). */
  label: string;
  /** `null` mostra um traco (o numero nao esta disponivel). */
  value: number | null;
};

/** Um premio do Hall of Fame Grow uP: uma medalha na carta. */
export type PlayerCardMedal = {
  /** Um dos desenhos de `HofMedal` (`trofeu`, `estrela`...). */
  icon: string;
  /** «uP Player · 2025»: dica ao passar o rato. */
  title: string;
};

export type PlayerCardProps = {
  /** O nickname (login). Nunca o nome verdadeiro. */
  name: string;
  tier: PlayerCardTier;
  /**
   * Streamer ativo no Stream Portal: um selo roxo em pe com o simbolo da
   * Twitch, sem texto, logo por baixo do traco do nivel (onde estava o icone uP, que saiu
   * quando o foil passou a ter o simbolo, 29-10-2026). A cor continua a ser a do nivel
   * (decisao do GrowuP, 22-10-2026).
   */
  streamer?: boolean;
  /** Numero de socio, no canto como o «overall» de uma carta FIFA. */
  number?: number | null;
  /** URL da foto (pode ser `blob:` ou `data:`). Sem foto, uma silhueta. */
  photoUrl?: string | null;
  /** Ate tres numeros, na fila de baixo. */
  stats?: readonly PlayerCardStat[];
  /**
   * Anos completos como membro. A partir de 5 a carta leva uma faixa no canto
   * (Bronze, Silver, Gold, Platinum: ver `seniorityFor`).
   */
  years?: number | null;
  /**
   * Premios do Hall of Fame, do mais recente para o mais antigo: uma fila de
   * medalhas no rodape, no lugar do «GROW UP ESPORTS» (ate 5; com mais, 4 e «+N»).
   */
  medals?: readonly PlayerCardMedal[];
  /** Largura em pixeis; a altura acompanha (proporcao 5:7). */
  width?: number;
  /**
   * Efeito holografico no ecra (inclinacao, reflexo e foil uP por tipo; ver
   * `holo.ts`). Ligado por omissao; a imagem descarregada sai sempre parada.
   * `'hover'`: a carta fica parada e as camadas do efeito so existem enquanto o
   * ponteiro esta em cima (para grelhas com muitas cartas).
   */
  holo?: boolean | 'hover';
  className?: string;
};

const W = 300;
const H = 420;
/** O escudo: cantos de cima cortados e a ponta em baixo, como as cartas FIFA. */
const SHIELD = 'M24 0H276L300 24V352L150 420L0 352V24Z';

/** O texto branco tem pelo menos 4,5:1 sobre a parte mais clara de cada fundo. */
const looks = {
  user: {
    from: CORES['card-user-from'],
    to: CORES['card-user-to'],
    ink: CORES['brand-white'],
    muted: CORES['brand-white'],
    accent: CORES['brand-red'],
    edge: CORES['card-user-edge'],
    label: 'UTILIZADOR',
  },
  member: {
    from: CORES['card-member-from'],
    to: CORES['card-member-to'],
    ink: CORES['brand-white'],
    muted: CORES['card-member-muted'],
    accent: CORES['brand-red'],
    edge: CORES['brand-red'],
    label: 'MEMBRO',
  },
  associate: {
    from: CORES['card-associate-from'],
    to: CORES['card-associate-to'],
    ink: CORES['brand-white'],
    muted: CORES['brand-white'],
    accent: CORES['brand-black'],
    edge: CORES['brand-white'],
    label: 'ASSOCIADO',
  },
} as const;

/**
 * Antiguidade como membro: uma faixa no canto de cima, com a cor do metal. O
 * fundo da carta continua a ser o do nivel.
 */
export type PlayerCardSeniority = 'bronze' | 'silver' | 'gold' | 'platinum';

const seniorities = [
  {
    key: 'platinum',
    min: 20,
    label: 'PLATINUM',
    from: CORES['tier-platinum-from'],
    to: CORES['tier-platinum-to'],
    ink: CORES['brand-charcoal'],
  },
  {
    key: 'gold',
    min: 15,
    label: 'GOLD',
    from: CORES['tier-gold-from'],
    to: CORES['tier-gold-to'],
    ink: CORES['brand-charcoal'],
  },
  {
    key: 'silver',
    min: 10,
    label: 'SILVER',
    from: CORES['tier-silver-from'],
    to: CORES['tier-silver-to'],
    ink: CORES['brand-charcoal'],
  },
  {
    key: 'bronze',
    min: 5,
    label: 'BRONZE',
    from: CORES['tier-bronze-from'],
    to: CORES['tier-bronze-to'],
    ink: CORES['brand-white'],
  },
] as const;

/** O escalao para `years` anos de membro, ou `null` abaixo de 5. */
export function seniorityFor(years: number | null | undefined): PlayerCardSeniority | null {
  if (years === null || years === undefined) return null;
  return seniorities.find((s) => years >= s.min)?.key ?? null;
}

/**
 * O foil uP parado, desenhado no proprio SVG para `holo="hover"`: imita a
 * camada `.gu-holo-shine` do styles.css em repouso (mesmo gradiente a 110 graus,
 * ja com o brightness/contrast/saturate aplicado as cores, mesmo azulejo e
 * mesma opacidade), para a carta nao "mudar" quando o rato chega. Pinta-se com
 * o resto do SVG e fica em cache, sem camadas por cima.
 */
const REST_FOIL = {
  user: {
    opacity: 0.15,
    stops: [
      [0, '#1F2733'],
      [0.2, '#ABB8C4'],
      [0.3, '#EBEBEB'],
      [0.4667, '#6A7889'],
      [0.6667, '#C9D1DB'],
      [1, '#1F2733'],
    ],
  },
  member: {
    opacity: 0.15,
    stops: [
      [0, '#410000'],
      [0.2, '#FF0E27'],
      [0.3333, '#FBB1B8'],
      [0.5, '#FF4B00'],
      [0.7, '#FF0D71'],
      [1, '#410000'],
    ],
  },
  associate: {
    opacity: 0.225,
    stops: [
      [0, '#FF1818'],
      [0.1667, '#FACA00'],
      [0.3333, '#00FF00'],
      [0.5, '#00E9FF'],
      [0.6667, '#3A3AFF'],
      [0.8333, '#FF1FFF'],
      [1, '#FF1818'],
    ],
  },
} as const satisfies Record<
  PlayerCardTier,
  { opacity: number; stops: readonly (readonly [number, string])[] }
>;

/** Medalhas: no maximo 5 na fila (com mais, 4 e «+N»). */
const MEDALS_MAX = 5;
const MEDAL_R = 13;
const MEDAL_Y = 371;
const MEDAL_GAP = 32;

function medalX(index: number, count: number): number {
  return 150 + (index - (count - 1) / 2) * MEDAL_GAP;
}

const DISPLAY = "'QB One', 'Russo One', system-ui, sans-serif";
const SUB = "Zekton, 'Chakra Petch', system-ui, sans-serif";

/**
 * Carta de membro estilo FIFA / eFootball: foto, nickname, nivel e tres
 * numeros. E um SVG, para ficar igual no ecra e na imagem descarregada
 * (`downloadPlayerCard`).
 */
export const PlayerCard = forwardRef<SVGSVGElement, PlayerCardProps>(function PlayerCard(
  {
    name,
    tier,
    streamer = false,
    number,
    photoUrl,
    stats = [],
    years,
    medals = [],
    width = 300,
    holo = true,
    className,
  },
  ref,
) {
  const effect = useHolo();
  const restFoil = holo === 'hover' && !effect.live;
  const foil = REST_FOIL[tier];
  const id = useId().replace(/:/g, '');
  const t = looks[tier];
  const shown = stats.slice(0, 3);
  const upper = name.toUpperCase();
  // Nicknames longos encolhem para caber na faixa, sem cortar.
  const nameSize = upper.length <= 10 ? 30 : upper.length <= 14 ? 24 : 20;
  const squeeze = upper.length > 18;
  const seniority = seniorities.find((x) => x.key === seniorityFor(years));
  const shownMedals = medals.length > MEDALS_MAX ? medals.slice(0, MEDALS_MAX - 1) : medals;
  const moreMedals = medals.length - shownMedals.length;
  const label = `Carta de ${name}, ${t.label.toLowerCase()}${streamer ? ', streamer' : ''}${
    number ? `, socio n.o ${String(number)}` : ''
  }${seniority ? `, ${seniority.label.toLowerCase()} (${String(seniority.min)}+ anos)` : ''}${
    medals.length > 0
      ? `, ${String(medals.length)} ${medals.length === 1 ? 'premio' : 'premios'} Grow uP`
      : ''
  }`;

  const card = (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${String(W)} ${String(H)}`}
      width={width}
      height={Math.round((width * H) / W)}
      role="img"
      aria-label={label}
      className={holo ? undefined : className}
      data-gu-player-card=""
    >
      <defs>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor={t.from} />
          <stop offset="1" stopColor={t.to} />
        </linearGradient>
        <linearGradient id={`${id}-fade`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.7" stopColor="#fff" stopOpacity="1" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <mask id={`${id}-photo-mask`}>
          <rect x="100" y="26" width="184" height="214" fill={`url(#${id}-fade)`} />
        </mask>
        {seniority ? (
          <linearGradient id={`${id}-ribbon`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={seniority.to} />
            <stop offset="0.5" stopColor={seniority.from} />
            <stop offset="1" stopColor={seniority.to} />
          </linearGradient>
        ) : null}
        {medals.length > 0 ? <HofMedalGradient id={`${id}-gold`} /> : null}
        {/* Textura, igual em todos os niveis: brilho, reflexo, favo, riscas, grao, sombra. */}
        <radialGradient id={`${id}-glow`} cx="0.25" cy="0.08" r="0.75">
          <stop offset="0" stopColor="#fff" stopOpacity={EFEITOS['carta-brilho']} />
          <stop offset="0.55" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-sheen`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0.3" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.38" stopColor="#fff" stopOpacity={EFEITOS['carta-reflexo']} />
          <stop offset="0.46" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={`${id}-shade`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.5" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity={EFEITOS['carta-sombra']} />
        </linearGradient>
        <pattern id={`${id}-hex`} width="18" height="31.2" patternUnits="userSpaceOnUse">
          <path
            d="M9 0L18 5.2V15.6L9 20.8L0 15.6V5.2Z M9 20.8V31.2"
            fill="none"
            stroke="#fff"
            strokeOpacity={EFEITOS['carta-favo']}
            strokeWidth="0.8"
          />
        </pattern>
        <pattern
          id={`${id}-lines`}
          width="7"
          height="7"
          patternUnits="userSpaceOnUse"
          patternTransform="skewX(-14)"
        >
          <rect width="1.2" height="7" fill="#fff" opacity={EFEITOS['carta-riscas']} />
        </pattern>
        {/* Grao: um ladrilho pre-feito (o feTurbulence de antes, igual), em vez de o calcular em cada carta. */}
        <pattern id={`${id}-grain`} width="128" height="128" patternUnits="userSpaceOnUse">
          <image href={grain} width="128" height="128" data-grain="" />
        </pattern>
        <clipPath id={`${id}-shield`}>
          <path d={SHIELD} />
        </clipPath>
        {restFoil ? (
          <>
            {/* O gradiente do foil: 300% da carta, centrado, periodo de 30% (ver styles.css). */}
            <linearGradient
              id={`${id}-foil`}
              gradientUnits="userSpaceOnUse"
              spreadMethod="repeat"
              x1="-449.84"
              y1="-8.32"
              x2="-89.94"
              y2="122.67"
            >
              {foil.stops.map(([offset, color]) => (
                <stop key={offset} offset={offset} stopColor={color} />
              ))}
            </linearGradient>
            <radialGradient
              id={`${id}-foil-light`}
              gradientUnits="userSpaceOnUse"
              cx="150"
              cy="126"
              r="330"
            >
              <stop offset="0" stopColor="#EBEBEB" stopOpacity="0.9" />
              <stop offset="0.22" stopColor="#EBEBEB" stopOpacity="0.25" />
              <stop offset="0.5" stopColor="#EBEBEB" stopOpacity="0" />
            </radialGradient>
            <pattern
              id={`${id}-foil-tile`}
              x="127"
              y="164"
              width="46"
              height="92"
              patternUnits="userSpaceOnUse"
            >
              <image href={holoUp} width="46" height="92" />
            </pattern>
            <mask id={`${id}-foil-mask`} style={{ maskType: 'alpha' }}>
              <rect width={W} height={H} fill={`url(#${id}-foil-tile)`} />
            </mask>
            {/* O escudo sem a zona da foto, como a arte de uma reverse. */}
            <clipPath id={`${id}-foil-clip`}>
              <path d={`${SHIELD} M100 26H284V240H100Z`} clipRule="evenodd" />
            </clipPath>
            {photoUrl ? (
              <>
                {/* A foto so com o alfa, a preto: tapa o foil onde a foto e opaca. */}
                <filter id={`${id}-photo-alpha`} colorInterpolationFilters="sRGB">
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
                  />
                </filter>
                {/* Zona da foto com o mesmo desvanecer, menos o alfa da foto (como .gu-holo-shine-photo). */}
                <mask id={`${id}-foil-photo-mask`}>
                  <rect x="100" y="26" width="184" height="214" fill={`url(#${id}-fade)`} />
                  <image
                    href={photoUrl}
                    x="100"
                    y="26"
                    width="184"
                    height="214"
                    preserveAspectRatio="xMidYMid slice"
                    filter={`url(#${id}-photo-alpha)`}
                  />
                </mask>
              </>
            ) : null}
          </>
        ) : null}
      </defs>

      <g clipPath={`url(#${id}-shield)`}>
        <path d={SHIELD} fill={`url(#${id}-bg)`} />
        <g data-texture="">
          <rect width={W} height={H} fill={`url(#${id}-hex)`} />
          <rect width={W} height={H} fill={`url(#${id}-lines)`} />
          <rect
            width={W}
            height={H}
            fill={`url(#${id}-grain)`}
            style={{ mixBlendMode: 'overlay' }}
          />
          <rect width={W} height={H} fill={`url(#${id}-glow)`} />
          <rect width={W} height={H} fill={`url(#${id}-sheen)`} />
          <rect width={W} height={H} fill={`url(#${id}-shade)`} />
        </g>
        {/* Os tres traços inclinados do manual (-14 graus), atras da foto. */}
        <g transform="skewX(-14)" opacity={EFEITOS['carta-barras']}>
          <rect x="250" y="0" width="16" height="250" fill={t.accent} />
          <rect x="276" y="0" width="16" height="250" fill={t.ink} />
          <rect x="302" y="0" width="16" height="250" fill={t.accent} />
        </g>

        {photoUrl ? (
          <image
            href={photoUrl}
            x="100"
            y="26"
            width="184"
            height="214"
            preserveAspectRatio="xMidYMid slice"
            mask={`url(#${id}-photo-mask)`}
          />
        ) : (
          <g fill={t.muted} opacity={EFEITOS['carta-silhueta']} data-silhouette="">
            <circle cx="186" cy="104" r="44" />
            <path d="M104 240C104 182 140 160 186 160C232 160 268 182 268 240Z" />
          </g>
        )}

        {/* Antiguidade: faixa a 45 graus no canto de cima, paralela ao corte do escudo. */}
        {seniority ? (
          <g transform="rotate(45 258 42)" data-seniority={seniority.key}>
            <rect x="168" y="27" width="180" height="30" fill={`url(#${id}-ribbon)`} />
            <rect x="168" y="27" width="180" height="1.5" fill="#000" opacity="0.25" />
            <rect x="168" y="55.5" width="180" height="1.5" fill="#000" opacity="0.25" />
            <text
              x="258"
              y="41"
              textAnchor="middle"
              fontFamily={SUB}
              fontWeight="700"
              fontSize="11"
              letterSpacing="1.5"
              fill={seniority.ink}
            >
              {seniority.label}
            </text>
            <text
              x="258"
              y="52"
              textAnchor="middle"
              fontFamily={SUB}
              fontWeight="700"
              fontSize="7.5"
              letterSpacing="1.2"
              fill={seniority.ink}
            >
              {`+${String(seniority.min)} ANOS`}
            </text>
          </g>
        ) : null}
      </g>

      <path
        d={SHIELD}
        fill="none"
        stroke={t.edge}
        strokeOpacity={EFEITOS['carta-contorno']}
        strokeWidth="3"
      />
      <path
        d="M28 8H272L292 28V347L150 410L8 347V28Z"
        fill="none"
        stroke="#fff"
        strokeOpacity="0.18"
        strokeWidth="1"
      />

      {/* Canto: numero de socio e nivel, como o overall e a posicao. */}
      <text
        x="52"
        y="78"
        textAnchor="middle"
        fontFamily={DISPLAY}
        fontSize={number && number >= 1000 ? 32 : 44}
        fill={t.ink}
      >
        {number ? String(number) : '—'}
      </text>
      <text
        x="52"
        y="100"
        textAnchor="middle"
        fontFamily={SUB}
        fontWeight="700"
        fontSize="11"
        letterSpacing="1.5"
        fill={t.muted}
      >
        {t.label}
      </text>
      <rect x="34" y="112" width="36" height="2" fill={t.accent} />
      {streamer ? (
        <g data-streamer-badge="">
          {/* Selo em pe so com o simbolo da Twitch. Por baixo fica espaco livre. */}
          <rect
            x="32"
            y="124"
            width="40"
            height="50"
            rx="5"
            fill={CORES['card-streamer-badge']}
            stroke={CORES['brand-white']}
            strokeWidth="1.5"
          />
          <path
            transform="translate(35.2 132.2) scale(1.4)"
            d="M6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0ZM20.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714ZM11.571 4.714h1.715v5.143H11.57ZM16.286 4.714H18v5.143h-1.714Z"
            fill={CORES['brand-white']}
            fillRule="evenodd"
          />
        </g>
      ) : null}

      {/* Faixa do nome. */}
      <text
        x="150"
        y="276"
        textAnchor="middle"
        fontFamily={DISPLAY}
        fontSize={nameSize}
        fill={t.ink}
        {...(squeeze ? { textLength: 256, lengthAdjust: 'spacingAndGlyphs' } : {})}
      >
        {upper}
      </text>
      <rect x="40" y="290" width="220" height="2" fill={t.ink} opacity="0.35" />

      {/* Numeros. */}
      {shown.map((stat, i) => {
        const cx = shown.length === 1 ? 150 : 70 + (160 / (shown.length - 1)) * i;
        return (
          <g key={stat.label}>
            <text
              x={cx}
              y="328"
              textAnchor="middle"
              fontFamily={DISPLAY}
              fontSize="28"
              fill={t.ink}
            >
              {stat.value === null ? '—' : String(stat.value)}
            </text>
            <text
              x={cx}
              y="346"
              textAnchor="middle"
              fontFamily={SUB}
              fontWeight="700"
              fontSize="10"
              letterSpacing="1.2"
              fill={t.muted}
            >
              {stat.label.toUpperCase()}
            </text>
          </g>
        );
      })}

      {/* Medalhas do Hall of Fame no rodape, no lugar do «GROW UP ESPORTS». */}
      {medals.length > 0 ? (
        <g data-medals={String(medals.length)}>
          {shownMedals.map((m, i) => (
            <HofMedalGlyph
              key={`${m.title}-${String(i)}`}
              icon={m.icon}
              cx={medalX(i, shownMedals.length + (moreMedals > 0 ? 1 : 0))}
              cy={MEDAL_Y}
              r={MEDAL_R}
              gradientId={`${id}-gold`}
              title={m.title}
            />
          ))}
          {moreMedals > 0 ? (
            <g>
              <title>{`Mais ${String(moreMedals)} ${moreMedals === 1 ? 'premio' : 'premios'}`}</title>
              <circle
                cx={medalX(shownMedals.length, shownMedals.length + 1)}
                cy={MEDAL_Y}
                r={MEDAL_R}
                fill={t.ink}
                fillOpacity="0.15"
                stroke={t.ink}
                strokeOpacity="0.5"
              />
              <text
                x={medalX(shownMedals.length, shownMedals.length + 1)}
                y={MEDAL_Y + 4}
                textAnchor="middle"
                fontFamily={SUB}
                fontWeight="700"
                fontSize="11"
                fill={t.ink}
              >
                {`+${String(moreMedals)}`}
              </text>
            </g>
          ) : null}
        </g>
      ) : null}

      {medals.length === 0 ? (
        <text
          x="150"
          y="386"
          textAnchor="middle"
          fontFamily={SUB}
          fontWeight="700"
          fontSize="9"
          letterSpacing="2.5"
          fill={t.muted}
        >
          GROW UP ESPORTS
        </text>
      ) : null}

      {restFoil ? (
        <g
          data-rest-foil=""
          clipPath={`url(#${id}-foil-clip)`}
          mask={`url(#${id}-foil-mask)`}
          opacity={foil.opacity}
          style={{ mixBlendMode: 'color-dodge' }}
        >
          <rect width={W} height={H} fill={`url(#${id}-foil)`} />
          <rect width={W} height={H} fill={`url(#${id}-foil-light)`} />
        </g>
      ) : null}
      {restFoil && photoUrl ? (
        // Foil tambem por tras da foto, onde ela e transparente (PNG recortado),
        // como a camada .gu-holo-shine-photo com o rato por cima.
        <g
          data-rest-foil-photo=""
          mask={`url(#${id}-foil-mask)`}
          opacity={foil.opacity}
          style={{ mixBlendMode: 'color-dodge' }}
        >
          <g mask={`url(#${id}-foil-photo-mask)`}>
            <rect x="100" y="26" width="184" height="214" fill={`url(#${id}-foil)`} />
            <rect x="100" y="26" width="184" height="214" fill={`url(#${id}-foil-light)`} />
          </g>
        </g>
      ) : null}
    </svg>
  );
  if (!holo) return card;
  const sparkle = seniority !== undefined && seniority.min >= 15;
  const layers = holo !== 'hover' || effect.live;
  return (
    <span
      ref={effect.ref}
      className={cx('gu-holo', className)}
      style={{ '--gu-holo-w': `${String(width)}px` } as CSSProperties}
      data-holo={tier}
      data-holo-idle={layers ? undefined : ''}
      {...effect.handlers}
    >
      <span className="gu-holo-card">
        {card}
        {layers ? <span className="gu-holo-fx gu-holo-shine" aria-hidden="true" /> : null}
        {layers && photoUrl ? (
          <span
            className="gu-holo-fx gu-holo-shine gu-holo-shine-photo"
            style={{ '--gu-holo-photo': `url(${JSON.stringify(photoUrl)})` } as CSSProperties}
            aria-hidden="true"
          />
        ) : null}
        {layers && sparkle ? (
          <span className="gu-holo-fx gu-holo-sparkle" aria-hidden="true" />
        ) : null}
        {layers ? <span className="gu-holo-fx gu-holo-glare" aria-hidden="true" /> : null}
      </span>
    </span>
  );
});
